import { FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { CheckCircle2 } from "lucide-react";
import { acceptInvite } from "../api/signupClient";
import { BrandLogo } from "../components/BrandLogo";

export function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activated, setActivated] = useState(false);
  const [error, setError] = useState<string | null>(token ? null : "Invite token is missing");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError("Invite token is missing");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setSubmitting(true);
    try {
      await acceptInvite({ token, password });
      setActivated(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invite could not be accepted");
    } finally {
      setSubmitting(false);
    }
  }

  if (activated) {
    return (
      <main className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center text-center gap-5">
          <Link to="/" aria-label="MEATrix OrderFlow home">
            <BrandLogo className="h-10 w-auto mb-2" />
          </Link>
          <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <CheckCircle2 size={28} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 font-display">Account activated</h1>
            <p className="text-sm text-slate-500 mt-1">Your password is set. Sign in to continue.</p>
          </div>
          <a
            href="/login"
            className="w-full min-h-[44px] flex items-center justify-center font-bold text-sm text-white bg-[#012699] hover:bg-[#0b2981] rounded-xl transition-all shadow-sm"
          >
            Sign in
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-4"
      >
        <div className="flex flex-col items-center text-center mb-2">
          <Link to="/" aria-label="MEATrix OrderFlow home" className="mb-4">
            <BrandLogo className="h-10 w-auto" />
          </Link>
          <h1 className="text-xl font-bold text-slate-900 font-display">Set password</h1>
          <p className="text-sm text-slate-500 mt-1">
            Choose a strong password to activate your account.
          </p>
        </div>

        <input
          type="password"
          placeholder="Password (12+ characters, mixed case, symbol)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full min-h-[44px] px-3.5 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all"
          required
          autoFocus
        />

        <input
          type="password"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full min-h-[44px] px-3.5 py-2 text-sm bg-white border border-slate-300 rounded-xl outline-none focus:border-[#012699] focus:ring-3 focus:ring-[#012699]/15 transition-all"
          required
        />

        {error && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !token}
          className="w-full min-h-[44px] flex items-center justify-center font-bold text-sm text-white bg-[#012699] hover:bg-[#0b2981] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all shadow-sm cursor-pointer mt-1"
        >
          {submitting ? "Activating..." : "Activate account"}
        </button>

        <a
          href="/login"
          className="text-center text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors mt-1"
        >
          Back to sign in
        </a>
      </form>
    </main>
  );
}
